import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

// Date formatting
const formatDate = (dateString) => {
  try {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'MMM dd, yyyy HH:mm', { locale: enUS });
  } catch (e) {
    console.error('Date format error:', e);
    return 'Invalid date';
  }
};

// Conversation card component
const ConversationCard = ({ conversation, onClick, searchQuery, favorites = [], onToggleFavorite }) => {
  const isFavorite = favorites.includes(conversation.id);

  // Highlight search keywords
  const highlightText = (text, query) => {
    if (!query || !text || query.trim() === '') return text;
    
    try {
      const keywords = query.trim().split(/\s+/);
      let highlightedText = text;
      keywords.forEach(keyword => {
        if (keyword) {
          const regex = new RegExp(`(${keyword})`, 'gi');
          highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
        }
      });
      return highlightedText;
    } catch (e) {
      console.error('Highlight error:', e);
      return text;
    }
  };
  
  const contentWithHighlight = searchQuery && conversation.content 
    ? highlightText(conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : ''), searchQuery)
    : conversation.content 
      ? conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : '') 
      : 'No content';

  return (
    <Card className="conversation-card" sx={{ mb: 2 }} elevation={2}>
      <CardActionArea onClick={() => onClick(conversation)}>
        <CardContent>
          <Typography variant="h6" component="div" gutterBottom>
            {conversation.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {formatDate(conversation.timestamp)}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 1.5 }} 
            dangerouslySetInnerHTML={{ __html: contentWithHighlight }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip 
              label={conversation.sender || 'unknown'} 
              size="small" 
              color={conversation.sender === 'user' ? 'primary' : 'secondary'}
              sx={{ mb: 0.5 }}
            />
            {conversation.inContext !== undefined && (
              <Chip 
                label={conversation.inContext ? 'In Context' : 'Out of Context'} 
                size="small" 
                variant="outlined"
                sx={{ mb: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(conversation);
                }}
              />
            )}
            {conversation.imageData && (
              <Chip 
                label="Has Image" 
                size="small" 
                color="info"
                sx={{ mb: 0.5 }}
              />
            )}
            <Chip 
              icon={isFavorite ? <StarIcon /> : <StarOutlineIcon />} 
              label={isFavorite ? 'Favorited' : 'Favorite'}
              size="small" 
              color={isFavorite ? "warning" : "default"}
              variant="outlined"
              sx={{ mb: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite && onToggleFavorite(conversation.id);
              }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// Date group component
const DateGroup = ({ date, conversations, onConversationClick, searchQuery, favorites, onToggleFavorite }) => {
  const formatGroupDate = (dateString) => {
    try {
      if (!dateString) return 'No date';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, 'MMM dd, yyyy', { locale: enUS });
    } catch (e) {
      console.error('Date group format error:', e);
      return 'Invalid date';
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {formatGroupDate(date)}
      </Typography>
      
      {conversations.map((conversation, index) => (
        <ConversationCard 
          key={conversation.id || `conv-${index}`} 
          conversation={conversation} 
          onClick={onConversationClick}
          searchQuery={searchQuery}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </Box>
  );
};

// Filter panel component
const FilterPanel = ({ filters, onFilterChange, searchQuery, onSearch }) => {
  const [dateRange, setDateRange] = useState(filters.dateRange || '');
  const [tagFilter, setTagFilter] = useState(filters.tags[0] || '');
  const [keyword, setKeyword] = useState(searchQuery || '');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(filters.showOnlyFavorites || false);
  
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    setDateRange(value);
    
    if (value === 'custom') {
      setCustomDateDialogOpen(true);
    } else {
      onFilterChange({ dateRange: value });
    }
  };

  const handleTagFilterChange = (event) => {
    const value = event.target.value;
    setTagFilter(value);
    onFilterChange({ tags: value ? [value] : [] });
  };

  const handleKeywordChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    onSearch(value);
  };

  const applyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      onFilterChange({ 
        dateRange: 'custom',
        customStartDate,
        customEndDate
      });
    }
    setCustomDateDialogOpen(false);
  };

  const handleFavoriteFilterChange = (event) => {
    const checked = event.target.checked;
    setShowOnlyFavorites(checked);
    onFilterChange({ showOnlyFavorites: checked });
  };

  const clearFilters = () => {
    setDateRange('');
    setTagFilter('');
    setKeyword('');
    setShowOnlyFavorites(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
    onFilterChange({ 
      dateRange: '', 
      tags: [], 
      showOnlyFavorites: false,
      customStartDate: null,
      customEndDate: null
    });
    onSearch('');
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Filters & Search
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Search Keywords"
            value={keyword}
            onChange={handleKeywordChange}
            size="small"
            InputProps={{
              endAdornment: keyword && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={() => {
                      setKeyword('');
                      onSearch('');
                    }}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={handleDateRangeChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Tag</InputLabel>
            <Select
              value={tagFilter}
              label="Tag"
              onChange={handleTagFilterChange}
            >
              <MenuItem value="">All Tags</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="assistant">Assistant</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyFavorites}
                  onChange={handleFavoriteFilterChange}
                  size="small"
                />
              }
              label="Favorites Only"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
            >
              Clear
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Custom Date Range Dialog */}
      <Dialog open={customDateDialogOpen} onClose={() => setCustomDateDialogOpen(false)}>
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select the start and end dates for filtering.
          </DialogContentText>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Start Date</Typography>
              <DatePicker
                selected={customStartDate}
                onChange={setCustomStartDate}
                selectsStart
                startDate={customStartDate}
                endDate={customEndDate}
                placeholderText="Select start date"
                dateFormat="MMM dd, yyyy"
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>End Date</Typography>
              <DatePicker
                selected={customEndDate}
                onChange={setCustomEndDate}
                selectsEnd
                startDate={customStartDate}
                endDate={customEndDate}
                minDate={customStartDate}
                placeholderText="Select end date"
                dateFormat="MMM dd, yyyy"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDateDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyCustomDateRange} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Main Timeline View component
const EnTimelineView = ({ 
  conversations, 
  onConversationClick, 
  searchQuery, 
  filters,
  onFilterChange,
  onSearch,
  favorites,
  onToggleFavorite
}) => {
  // Group conversations by date
  const groupByDate = (conversations) => {
    const groups = {};
    
    conversations.forEach(conversation => {
      const date = conversation.timestamp ? new Date(conversation.timestamp).toDateString() : 'No date';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(conversation);
    });
    
    // Sort groups by date (newest first)
    const sortedGroups = Object.keys(groups)
      .sort((a, b) => {
        if (a === 'No date') return 1;
        if (b === 'No date') return -1;
        return new Date(b) - new Date(a);
      })
      .map(date => ({
        date,
        conversations: groups[date].sort((a, b) => 
          new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
        )
      }));
    
    return sortedGroups;
  };

  // Filter conversations based on current filters
  const getFilteredConversations = () => {
    let filtered = [...conversations];

    // Apply search filter
    if (searchQuery && searchQuery.trim()) {
      const keywords = searchQuery.trim().toLowerCase().split(/\s+/);
      filtered = filtered.filter(conversation => {
        const content = (conversation.content || '').toLowerCase();
        const title = (conversation.title || '').toLowerCase();
        return keywords.some(keyword => 
          content.includes(keyword) || title.includes(keyword)
        );
      });
    }

    // Apply date range filter
    if (filters.dateRange && filters.dateRange !== '') {
      const now = new Date();
      let startDate, endDate;

      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          break;
        case 'yesterday':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - now.getDay());
          startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
          endDate = new Date();
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date();
          break;
        case 'custom':
          if (filters.customStartDate && filters.customEndDate) {
            startDate = filters.customStartDate;
            endDate = new Date(filters.customEndDate);
            endDate.setDate(endDate.getDate() + 1);
          }
          break;
        default:
          // No specific date range filter
          break;
      }

      if (startDate && endDate) {
        filtered = filtered.filter(conversation => {
          const convDate = new Date(conversation.timestamp);
          return convDate >= startDate && convDate < endDate;
        });
      }
    }

    // Apply tag filter
    if (filters.tags && filters.tags.length > 0) {
      filtered = filtered.filter(conversation => 
        filters.tags.includes(conversation.sender)
      );
    }

    // Apply favorites filter
    if (filters.showOnlyFavorites) {
      filtered = filtered.filter(conversation => 
        favorites && favorites.includes(conversation.id)
      );
    }

    return filtered;
  };

  const filteredConversations = getFilteredConversations();
  const groupedConversations = groupByDate(filteredConversations);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Timeline View
      </Typography>
      
      <FilterPanel 
        filters={filters}
        onFilterChange={onFilterChange}
        searchQuery={searchQuery}
        onSearch={onSearch}
      />
      
      {groupedConversations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No conversations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or filters
          </Typography>
        </Box>
      ) : (
        groupedConversations.map((group, index) => (
          <DateGroup 
            key={group.date}
            date={group.date}
            conversations={group.conversations}
            onConversationClick={onConversationClick}
            searchQuery={searchQuery}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
          />
        ))
      )}
    </Box>
  );
};

export default EnTimelineView; 